import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import PbxContext from '../../../context/PbxContext';

import {
  accessLocalStorage,
  valuesAnalyze,
  changeViewForms,
  readInterfaces,
  stageIpChange,
  confirmIpChange,
  rollbackIpChange,
  ipChangeHealth,
} from '../../../services';
import FormIPSet from './formIpSetInterfaces';

import '../../../libs/bulma.min.css';
import './ChangeIp.css';

// Troca de IP com commit-confirm (Gap 2 do plano).
// 3 estados:
//   1. 'edit'     — formulário padrão, botão "Aplicar".
//   2. 'staging'  — countdown 120 s; botões "Confirmar" (após 10 s) e "Desfazer".
//   3. 'done'     — mostra resultado final.
// Se o navegador perder conexão no estado 'staging', o servidor reverte sozinho
// via systemd-run (--on-active=120s) e a UI, ao recarregar, mostra o IP antigo.
function ChangeIp() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsHidden } = getItensStateGlobal;

  const history = useHistory();

  const [phase, setPhase] = useState('edit');             // 'edit' | 'staging' | 'done'
  const [doneKind, setDoneKind] = useState(null);         // 'confirmed' | 'rolled_back' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [errorBlockers, setErrorBlockers] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [stageInfo, setStageInfo] = useState({ token: null, deadline: null, applied: null });
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [confirmUnlockSec, setConfirmUnlockSec] = useState(10);
  const [driverInfo, setDriverInfo] = useState({ driver: null, ready: null, version: null });
  const [rollbackFailedMarker, setRollbackFailedMarker] = useState(false);
  // Guarda contra duplo-clique: confirm/rollback são idempotentes só em estado
  // limpo. Se o user clica duas vezes em Confirmar, o segundo request pega o
  // arquivo já reescrito pelo primeiro → hash drift → CONFIG_TAMPERED →
  // rollback indevido. inflightRef evita disparo em paralelo mesmo antes do
  // React re-renderizar; submitting é apenas para o `disabled` visual.
  const [submitting, setSubmitting] = useState(false);
  const inflightRef = useRef(false);

  const [values, setValues] = useState({});
  const [validate, setValidate] = useState({});
  const [interfaces, setInterfaces] = useState([]);
  const [fullDataIfaces, setFullDataIfaces] = useState({});

  const countdownTimer = useRef(null);
  const unlockTimer = useRef(null);

  const { changeBorderImputToRedOrNormal, changeVisibityMessages } = changeViewForms;

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user } = dataUser;
    if (!user || user.role !== 'root' || user.idtenant !== 'vgs') {
      return history.push('/home');
    }
    const getNetworkData = await readInterfaces();
    const filterIfaces = getNetworkData.map((element) => {
      delete element['lo'];
      return element;
    });
    setInterfaces(Object.keys(...filterIfaces));
    Object.keys(...filterIfaces).forEach((i) => setValues((prev) => ({ ...prev, [i]: { iface: i } })));
    setFullDataIfaces(...filterIfaces);

    // Verifica se há um staging pendente (ex.: o operador recarregou a página).
    try {
      const health = await ipChangeHealth();
      if (health.status === 200 && health.data) {
        setDriverInfo({
          driver: health.data.driver || null,
          ready: typeof health.data.ready === 'boolean' ? health.data.ready : null,
          version: health.data.netplanVersion || null,
        });
        setRollbackFailedMarker(!!health.data.rollbackFailedMarker);
        if (health.data.stageLegacy) {
          setErrorMsg('Stage-file de schema antigo detectado. Operação bloqueada — siga o runbook de console (docs/runbooks/network-rollback.md).');
        }
        if (health.data.stagePending) {
          setPhase('staging');
          setStageInfo({ token: null, deadline: health.data.deadline, applied: null });
          // Usa secondsRemaining server-side se disponível (sem drift de clock).
          const left = typeof health.data.secondsRemaining === 'number'
            ? health.data.secondsRemaining
            : Math.max(0, Math.round((new Date(health.data.deadline).getTime() - Date.now()) / 1000));
          startCountdown(Date.now() + left * 1000);
        }
      }
    } catch (_) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const changeScreen = (check, name) => {
    changeBorderImputToRedOrNormal(check, name);
    changeVisibityMessages(check, `${name}-invalid-data`);
  };

  const handleChangeDataAnalyze = (name, value) => {
    const { isIP, isMask, checkNumber } = valuesAnalyze;
    const itemCheck = name.split('-')[1];
    // metric é opcional: vazio = neutro/válido (não pinta de vermelho).
    if (itemCheck === 'metric' && (value === '' || value === undefined || value === null)) {
      changeScreen(true, name);
      return true;
    }
    let ok = false;
    if (itemCheck === 'ip' || itemCheck === 'gw') ok = isIP(value);
    if (itemCheck === 'mask') ok = isMask(value);
    if (itemCheck === 'metric') ok = checkNumber(value);
    changeScreen(ok, name);
    return ok;
  };

  const handleChangeValues = (item) => {
    const { name, value } = item;
    const element = name.split('-');
    const interfaceName = element[0];
    const prop = element[1];
    const ok = handleChangeDataAnalyze(name, value);
    setValidate((prev) => ({ ...prev, [name]: ok }));
    setValues((prev) => ({
      ...prev,
      [interfaceName]: { ...(prev[interfaceName] || {}), iface: interfaceName, [prop]: value },
    }));
  };

  // Valores atuais (no kernel) de uma iface — usados como fallback quando o
  // operador não digita determinado campo. Permite o caso "mudar só a
  // métrica" sem forçar o operador a re-digitar ip/máscara/gateway.
  const currentValuesOf = (iface) => {
    const data = fullDataIfaces[iface] || {};
    return {
      ip: data.addrCidr ? data.addrCidr.split('/')[0] : (data.ipaddr || ''),
      mask: data.mask || '',
      gw: data.gateway || '',
      metric: data.metric != null ? String(data.metric) : '',
    };
  };

  // Para cada iface, decide se está "pronta para envio". Uma iface só é
  // "touched" quando o operador efetivamente digitou/selecionou algo nela
  // (ver post-mortem 2026-05-11 em formIpSetInterfaces.js). "Ready" exige
  // touched + (valor digitado OU valor atual do kernel) para ip/máscara/gw.
  // Campos digitados são validados; campos não digitados usam o valor atual
  // (que assumimos válido por já estar configurado no servidor).
  const computeIfaceState = () => {
    const list = [];
    for (const iface of interfaces) {
      const v = values[iface] || {};
      const cur = currentValuesOf(iface);
      const fieldOk = (f) => validate[`${iface}-${f}`] === true;
      const userTyped = (f) => v[f] !== undefined && v[f] !== '';
      const labels = { ip: 'IP', mask: 'Máscara', gw: 'Gateway', metric: 'Métrica' };
      const missing = [];
      ['ip', 'mask', 'gw'].forEach((f) => {
        if (userTyped(f)) {
          if (!fieldOk(f)) missing.push(`${labels[f]} (inválido)`);
        } else if (!cur[f]) {
          missing.push(labels[f]);
        }
      });
      if (userTyped('metric') && !fieldOk('metric')) missing.push(`${labels.metric} (numérica)`);
      const touched = ['ip', 'mask', 'gw', 'metric'].some((f) => userTyped(f));
      const ready = touched && missing.length === 0;
      list.push({ iface, touched, ready, missing });
    }
    return list;
  };

  const ifaceState = computeIfaceState();
  const readyIfaces = ifaceState.filter((s) => s.ready);
  const touchedIfaces = ifaceState.filter((s) => s.touched);
  const touchedNotReady = ifaceState.filter((s) => s.touched && !s.ready);
  // Backend processa UMA iface por vez (post-mortem 2026-05-11). Se o operador
  // tocar em mais de uma, bloqueamos no front com mensagem clara — assim ele
  // não fica confuso quando "alterar eth1" acaba mexendo em eth0.
  const multiTouched = touchedIfaces.length > 1;
  const disable = readyIfaces.length === 0 || multiTouched;

  const startCountdown = (deadlineMs) => {
    clearInterval(countdownTimer.current);
    clearInterval(unlockTimer.current);
    setConfirmUnlockSec(10);
    unlockTimer.current = setInterval(() => {
      setConfirmUnlockSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const tick = () => {
      const left = Math.max(0, Math.round((deadlineMs - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        clearInterval(countdownTimer.current);
        // Servidor reverteu sozinho; refresca UI.
        setTimeout(async () => {
          try {
            const h = await ipChangeHealth();
            if (h.status === 200 && !h.data.stagePending) {
              setPhase('done');
              setDoneKind('rolled_back');
            }
          } catch (_) {}
        }, 1500);
      }
    };
    tick();
    countdownTimer.current = setInterval(tick, 1000);
  };

  useEffect(() => () => {
    clearInterval(countdownTimer.current);
    clearInterval(unlockTimer.current);
  }, []);

  const applyChange = async () => {
    if (inflightRef.current) return;
    // Backend commit-confirm processa uma interface por vez (entry = data[0]).
    // Enviamos APENAS ifaces que o operador tocou (touched) — sem isso, ifaces
    // que estavam pré-preenchidas no kernel apareciam no payload e ofuscavam
    // a iface que o operador realmente quis mudar. Para campos não digitados,
    // usamos o valor atual do kernel como fallback (caso "mudar só a métrica").
    const payload = readyIfaces.map(({ iface }) => {
      const v = values[iface] || {};
      const cur = currentValuesOf(iface);
      const ip = v.ip !== undefined && v.ip !== '' ? v.ip : cur.ip;
      const mask = v.mask !== undefined && v.mask !== '' ? v.mask : cur.mask;
      const gw = v.gw !== undefined && v.gw !== '' ? v.gw : cur.gw;
      const metric = v.metric !== undefined && v.metric !== ''
        ? v.metric
        : cur.metric;
      const entry = { iface, ip, mask, gw };
      if (metric !== undefined && metric !== '') entry.metric = metric;
      return entry;
    });
    if (payload.length === 0) return;
    inflightRef.current = true;
    setSubmitting(true);
    setErrorMsg('');
    setErrorBlockers([]);
    setWarnings([]);
    try {
      const resp = await stageIpChange(payload);
      if (resp.status !== 202) {
        const data = resp.data || {};
        let msg = data.message || 'Falha ao aplicar.';
        let blockers = [];
        if (data.code === 'NO_NET_DRIVER' || data.code === 'NO_NETPLAN') {
          msg = 'Servidor sem netplan e sem ifupdown detectáveis. Verifique se /etc/network/interfaces existe ou se netplan está instalado.';
        } else if (data.code === 'STAGE_LEGACY_SCHEMA') {
          msg = 'Stage-file de schema antigo detectado. Use o runbook de console (docs/runbooks/network-rollback.md).';
        } else if (data.code === 'IFACE_NOT_SUPPORTED') {
          msg = 'Esta interface tem configuração não suportada pelo driver ifupdown desta entrega (apenas inet static puro é aceito).';
          blockers = Array.isArray(data.details) ? data.details : [];
        } else if (data.code === 'STAGE_ACTIVE' || data.code === 'STAGE_APPLYING') {
          msg = 'Já existe um stage de troca de IP em andamento. Aguarde rollback automático (2 min) ou confirme/reverta antes.';
        } else if (data.code === 'IFACE_NOT_IN_INTERFACES' || data.code === 'IFACE_NOT_IN_NETPLAN') {
          msg = `Interface não declarada no arquivo de configuração do driver (${driverInfo.driver || 'detectado'}).`;
        }
        setErrorMsg(msg);
        setErrorBlockers(blockers);
        return;
      }
      setStageInfo({
        token: resp.data.token,
        deadline: resp.data.deadline,
        applied: resp.data.applied,
      });
      setWarnings(resp.data.warnings || []);
      setPhase('staging');
      const initialLeft = typeof resp.data.secondsRemaining === 'number'
        ? resp.data.secondsRemaining
        : Math.max(0, Math.round((new Date(resp.data.deadline).getTime() - Date.now()) / 1000));
      startCountdown(Date.now() + initialLeft * 1000);
    } finally {
      inflightRef.current = false;
      setSubmitting(false);
    }
  };

  const doConfirm = async () => {
    if (inflightRef.current) return;
    if (!stageInfo.token) {
      setErrorMsg('Sessão expirada — sem token para confirmar.');
      return;
    }
    inflightRef.current = true;
    setSubmitting(true);
    try {
      const resp = await confirmIpChange(stageInfo.token);
      clearInterval(countdownTimer.current);
      clearInterval(unlockTimer.current);
      if (resp.status === 200) {
        setPhase('done');
        setDoneKind('confirmed');
      } else {
        setErrorMsg((resp.data && resp.data.message) || 'Falha ao confirmar.');
      }
    } finally {
      inflightRef.current = false;
      setSubmitting(false);
    }
  };

  const doRollback = async () => {
    if (inflightRef.current) return;
    if (!stageInfo.token) {
      setErrorMsg('Sessão expirada — sem token para reverter.');
      return;
    }
    inflightRef.current = true;
    setSubmitting(true);
    try {
      const resp = await rollbackIpChange(stageInfo.token);
      clearInterval(countdownTimer.current);
      clearInterval(unlockTimer.current);
      if (resp.status === 200) {
        setPhase('done');
        setDoneKind('rolled_back');
      } else {
        setErrorMsg((resp.data && resp.data.message) || 'Falha ao desfazer.');
      }
    } finally {
      inflightRef.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  return (
    <div className="change-ip-container">
      <div className="change-ip-header">
        <div className="change-ip-title-wrapper">
          <svg className="change-ip-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div>
            <h1 className="change-ip-title">Alterar IP</h1>
            <p className="change-ip-subtitle">
              Commit-confirm com rollback automático em 2 minutos
              {driverInfo.driver && (
                <span style={{ marginLeft: 8, fontSize: '0.85em', opacity: 0.8 }}>
                  · Driver: <code>{driverInfo.driver}</code>
                  {driverInfo.version && <> ({driverInfo.version})</>}
                  {driverInfo.ready === false && <> · <strong style={{ color: '#c0392b' }}>indisponível</strong></>}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {rollbackFailedMarker && (
        <div className="alert-message alert-danger">
          <div className="alert-body">
            <strong>Rollback automático falhou em execução anterior.</strong>{' '}
            Consulte <code>/var/log/vigoscloud-ip-rollback.log</code> e remova{' '}
            <code>/var/run/vigoscloud-ip-rollback-FAILED</code> após investigar.
            A rede pode estar em estado inconsistente.
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="alert-message alert-danger">
          <div className="alert-body">
            <strong>Erro:</strong> {errorMsg}
            {errorBlockers.length > 0 && (
              <ul style={{ marginTop: 8 }}>
                {errorBlockers.map((b, i) => (
                  <li key={i}><code>{b.key}</code> ({b.scope}): {b.reason}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {phase === 'edit' && (
        <div className="change-ip-content">
          <div className="alert-message alert-info">
            <div className="alert-body">
              <strong>Atenção:</strong> Após aplicar você tem <strong>2 minutos</strong> para confirmar.
              Se perder o acesso (ex.: configuração incorreta), o servidor voltará sozinho ao IP anterior.
            </div>
          </div>
          <div id="change-ip-div" className="interfaces-list">
            {interfaces.map((iface) => (
              <FormIPSet
                key={iface}
                iface={iface}
                data={fullDataIfaces[iface]}
                handleChangeValues={handleChangeValues}
              />
            ))}
          </div>
          <div className="change-ip-actions">
            <button className="btn-submit-ip" type="submit" disabled={disable || submitting} onClick={applyChange}>
              <span>{submitting ? 'Aplicando…' : 'Aplicar (com confirmação)'}</span>
            </button>
          </div>
          {disable && (
            <div className={`alert-message ${multiTouched ? 'alert-warning' : 'alert-info'}`} style={{ marginTop: 12 }}>
              <div className="alert-body">
                {multiTouched ? (
                  <>
                    <strong>Você modificou mais de uma interface.</strong> Só é possível alterar
                    {' '}<strong>uma por vez</strong>. Limpe os campos das interfaces que NÃO deseja
                    alterar — você tocou em: {touchedIfaces.map((s, i) => (
                      <React.Fragment key={s.iface}>
                        {i > 0 && ', '}<code>{s.iface}</code>
                      </React.Fragment>
                    ))}.
                  </>
                ) : touchedNotReady.length > 0 ? (
                  <>
                    <strong>Preencha os campos faltantes para habilitar o botão:</strong>
                    <ul>
                      {touchedNotReady.map((s) => (
                        <li key={s.iface}><code>{s.iface}</code>: {s.missing.join(', ')}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <span>Preencha <strong>IP</strong>, <strong>Máscara</strong> e <strong>Gateway</strong> de pelo menos uma interface. Métrica é opcional.</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {phase === 'staging' && (
        <div className="change-ip-content">
          <div className="alert-message alert-warning">
            <div className="alert-body">
              <strong>Mudança aplicada em modo de teste.</strong><br />
              Tempo restante para confirmar: <strong>{secondsLeft}s</strong><br />
              {stageInfo.applied && (
                <>Interface <code>{stageInfo.applied.iface}</code>, novo IP <code>{stageInfo.applied.newIp}/{stageInfo.applied.newPrefix}</code>{stageInfo.applied.newGw && <> via <code>{stageInfo.applied.newGw}</code></>}.</>
              )}
            </div>
          </div>
          {warnings.length > 0 && (
            <div className="alert-message alert-warning">
              <div className="alert-body">
                <strong>Avisos de pré-validação:</strong>
                <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            </div>
          )}
          <div className="change-ip-actions">
            <button
              className="btn-submit-ip"
              onClick={doConfirm}
              disabled={confirmUnlockSec > 0 || submitting}
              title={confirmUnlockSec > 0 ? `Aguarde ${confirmUnlockSec}s` : (submitting ? 'Operação em andamento…' : 'Confirmar a mudança')}
            >
              <span>
                {submitting
                  ? 'Confirmando…'
                  : (confirmUnlockSec > 0 ? `Confirmar (em ${confirmUnlockSec}s)` : 'Confirmar')}
              </span>
            </button>
            <button
              className="btn-cancel-ip"
              onClick={doRollback}
              disabled={submitting}
              title={submitting ? 'Operação em andamento…' : 'Reverter agora ao IP anterior'}
            >
              <span>{submitting ? 'Aguarde…' : 'Desfazer agora'}</span>
            </button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div className="change-ip-content">
          {doneKind === 'confirmed' && (
            <div className="alert-message alert-success">
              <div className="alert-body">Troca de IP <strong>confirmada</strong>.</div>
            </div>
          )}
          {doneKind === 'rolled_back' && (
            <div className="alert-message alert-warning">
              <div className="alert-body">Mudança foi <strong>revertida</strong> — servidor está no IP anterior.</div>
            </div>
          )}
          <div className="change-ip-actions">
            <button className="btn-submit-ip" onClick={() => window.location.reload()}>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangeIp;
